import type { TradeCycleVersion } from '@festina/unitlink-general-types/unitlink'
import { CircularSpinner } from '@festina-life/ui/feedback'
import { withSuspense } from '@festina-life/ui/libs'
import { Suspense, useEffect, useMemo, useState } from 'react'

import { LifeErrorFallback } from '../../components/errorFallback/LifeErrorFallback'
import { LifePage } from '../../components/page/LifePage'
import { useTime } from '../../hooks/useTime'
import { useHook } from '../../lenteHooks'
import { withLifeErrorBoundary } from '../../libs/withLifeErrorBoundary'
import { TradeCycleCard } from './TradeCycleCard'
import { getDefaultTradeCycleFilterValues } from './tradeCycleFilterUtils'
import { TradeCycleOverview } from './TradeCycleOverview'
import { TradeCycleTradePriceSellAndBuy } from './TradeCycleTradePriceSellAndBuy'
import { TradeOrdersCard } from './TradeOrdersCard'
import { useGetTradeCycleChains } from './useGetTradeCycleChains'

const TradeCyclesPageInternal = () => {
  const [tradeCycle, setTradeCycle] = useState<TradeCycleVersion>()
  const { tradeCycleId } = useHook('routing.general.useTradeCycleParams')({
    isOptional: true,
  })
  const { data: time } = useTime()
  const [filterValues, setFilterValues] = useState(
    getDefaultTradeCycleFilterValues(time),
  )

  const tradeCycleChains = useGetTradeCycleChains(filterValues)

  const nextTradeCycle = useMemo(() => {
    if (!tradeCycle) {
      return undefined
    }

    const chain = tradeCycleChains.find((cycles) =>
      cycles.some((cycle) => cycle.tradeCycleId === tradeCycle.tradeCycleId),
    )

    if (!chain) {
      return undefined
    }

    const currentIndex = chain.findIndex(
      (cycle) => cycle.tradeCycleId === tradeCycle.tradeCycleId,
    )

    return chain[currentIndex - 1] // The sort order is newest-to-oldest, so the next trade cycle is the previous one in the array.
  }, [tradeCycle, tradeCycleChains])

  useEffect(() => {
    if (!tradeCycleId) {
      return
    }

    setTradeCycle(
      tradeCycleChains
        .flat()
        .find((cycle) => cycle.tradeCycleId === tradeCycleId),
    )
  }, [tradeCycleChains, tradeCycleId])

  return (
    <LifePage
      ContentRight={
        tradeCycle?.tradeCycleId ? (
          <>
            <Suspense fallback={<CircularSpinner />}>
              <TradeCycleCard
                tradeCycle={tradeCycle}
                nextTradeCycle={nextTradeCycle}
                key={tradeCycle.tradeCycleId}
              />
            </Suspense>
            <TradeOrdersCard tradeCycleId={tradeCycle.tradeCycleId} />
            <Suspense fallback={<CircularSpinner />}>
              <TradeCycleTradePriceSellAndBuy
                tradeCycleId={tradeCycle?.tradeCycleId}
              />
            </Suspense>
          </>
        ) : null
      }
    >
      <TradeCycleOverview
        tradeCycleChains={tradeCycleChains}
        selectedTradeCycle={tradeCycle}
        filterValues={filterValues}
        onSearch={setFilterValues}
      />
    </LifePage>
  )
}

export const TradeCyclesPage = withLifeErrorBoundary(
  withSuspense(TradeCyclesPageInternal),
  {
    FallbackComponent: LifeErrorFallback,
  },
)
